/**
 * Wishlist: click delegation, customer merge, wishlist page (griglia = section Liquid + Section Rendering).
 */
(function () {
  'use strict';

  const STOREFRONT_GQL_PATH = '/api/2023-10/graphql.json';

  const S = () => window.WishlistStorage;
  const T = () => window.WishlistTracking;
  const U = () => window.WishlistUI;

  /** Accetta id numerico o gid://shopify/Product/123 (es. da metafield cliente). */
  function extractNumericProductId(rawId) {
    const s = S().normalizeProductId(rawId);
    const fromGid = s.match(/Product\/(\d+)/);
    if (fromGid) return fromGid[1];
    return /^\d+$/.test(s) ? s : '';
  }

  function productGidFromNumericId(numericId) {
    const n = extractNumericProductId(numericId);
    return n ? `gid://shopify/Product/${n}` : null;
  }

  function runLoggedInMerge() {
    const c = window.wishlistCustomer;
    if (!c || !c.loggedIn || !S()) return;
    const merged = S().mergeWithLocal(c.initialWishlist);
    const local = S().getWishlist();
    if (!S().listsEqual(merged, local)) {
      S().saveWishlist(merged, { source: 'customer_merge' });
    }
  }

  function storefrontFetch(query, variables) {
    const token = typeof window.storefrontAccessToken === 'string' ? window.storefrontAccessToken.trim() : '';
    if (!token) {
      return Promise.reject(new Error('MISSING_TOKEN'));
    }
    return fetch(STOREFRONT_GQL_PATH, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': token,
      },
      body: JSON.stringify({ query, variables }),
    }).then(async (res) => {
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = json && json.errors && json.errors[0] && json.errors[0].message;
        throw new Error(msg || `HTTP ${res.status}`);
      }
      if (json.errors && json.errors.length) {
        throw new Error(json.errors[0].message || 'GraphQL error');
      }
      return json.data;
    });
  }

  const HANDLES_QUERY = `
    query WishlistHandles($ids: [ID!]!) {
      nodes(ids: $ids) {
        ... on Product {
          id
          handle
        }
      }
    }
  `;

  /** File sections/wishlist-product-card-ajax.liquid → Section Rendering id statico. */
  const WISHLIST_CARD_SECTION_ID = 'wishlist-product-card-ajax';

  function productPageUrlForHandle(handle) {
    const h = encodeURIComponent(handle);
    if (window.Shopify && window.Shopify.routes && typeof window.Shopify.routes.root === 'string') {
      const root = window.Shopify.routes.root;
      return `${root}products/${h}`;
    }
    return `/products/${h}`;
  }

  function extractProductCardLi(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const li = doc.querySelector('li.collection-product-card');
    return li ? document.importNode(li, true) : null;
  }

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function getWishlistSectionMount() {
    return document.querySelector('[data-wishlist-section-render]');
  }

  function getWishlistSectionShell() {
    const m = getWishlistSectionMount();
    if (!m) return null;
    let el = m.closest('.shopify-section');
    if (el) return el;
    let n = m.parentElement;
    while (n && n !== document.body) {
      if (n.id && String(n.id).indexOf('shopify-section-') === 0) return n;
      n = n.parentElement;
    }
    return null;
  }

  function emptyWishlistHtml(wl) {
    return `
      <div class="wishlist-page__empty" role="status">
        <div class="wishlist-page__empty-icon" aria-hidden="true">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 21s-6.716-4.24-9-8.5C.78 10.028 2.12 6.5 5.09 5.5 7.3 4.78 9.52 5.78 12 8c2.48-2.22 4.7-3.22 6.91-2.5 2.97 1 4.33 4.53 3.09 7-2.284 4.26-9 8.5-9 8.5z"
              stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <h2 class="wishlist-page__empty-title">${escapeHtml(wl.emptyTitle || '')}</h2>
        <p class="wishlist-page__empty-text">${escapeHtml(wl.emptyText || '')}</p>
        ${
          wl.emptyCtaUrl
            ? `<a class="button button--secondary wishlist-page__empty-cta" href="${escapeHtml(wl.emptyCtaUrl)}">${escapeHtml(wl.emptyCtaLabel || '')}</a>`
            : ''
        }
      </div>`;
  }

  function renderWishlistProductGridPage() {
    if (!S()) return;
    const emptySlot = document.getElementById('wishlist-empty-slot');
    const mount = getWishlistSectionMount();
    const sectionShell = getWishlistSectionShell();
    if (!emptySlot && !sectionShell) return;

    const wl = window.wishlistStrings || {};
    const ids = S().getWishlist();

    if (!sectionShell || !mount) {
      if (emptySlot) {
        if (!ids.length) emptySlot.innerHTML = emptyWishlistHtml(wl);
        else
          emptySlot.innerHTML = `<div class="wishlist-page__error" role="alert">${escapeHtml(
            wl.errorLoad || 'Could not find wishlist grid on this page.'
          )}</div>`;
      }
      return;
    }

    const sectionId = mount.getAttribute('data-wishlist-section-render');
    if (!sectionId) return;

    if (!ids.length) {
      sectionShell.setAttribute('hidden', '');
      if (emptySlot) emptySlot.innerHTML = emptyWishlistHtml(wl);
      return;
    }

    sectionShell.removeAttribute('hidden');
    if (emptySlot) {
      emptySlot.innerHTML = `<div class="wishlist-page__loading" role="status">${escapeHtml(wl.loading || '…')}</div>`;
    }

    const gids = ids.map(productGidFromNumericId).filter(Boolean);

    storefrontFetch(HANDLES_QUERY, { ids: gids })
      .then(async (data) => {
        const nodes = (data && data.nodes) || [];
        const byId = new Map();
        nodes.forEach((node) => {
          if (!node || !node.id || !node.handle) return;
          const match = String(node.id).match(/Product\/(\d+)/);
          if (match) byId.set(match[1], node.handle);
        });
        const handles = ids.map((id) => byId.get(extractNumericProductId(id))).filter(Boolean);

        if (!handles.length) {
          sectionShell.setAttribute('hidden', '');
          if (emptySlot) emptySlot.innerHTML = emptyWishlistHtml(wl);
          return;
        }

        const ul = mount.querySelector('ul.products-grid-section__wrapper');
        if (!ul) {
          throw new Error('NO_GRID_UL');
        }

        const fetchOpts = { cache: 'no-store' };
        const htmlResponses = await Promise.all(
          handles.map((handle, idx) =>
            fetch(
              `${productPageUrlForHandle(handle)}?section_id=${encodeURIComponent(WISHLIST_CARD_SECTION_ID)}&_wlp=${Date.now()}-${idx}`,
              fetchOpts
            ).then((r) => (r.ok ? r.text() : ''))
          )
        );

        ul.innerHTML = '';
        let cardsInserted = 0;
        htmlResponses.forEach((html) => {
          const li = extractProductCardLi(html);
          if (li) {
            ul.appendChild(li);
            cardsInserted += 1;
          }
        });

        if (cardsInserted === 0) {
          sectionShell.setAttribute('hidden', '');
          if (emptySlot) emptySlot.innerHTML = emptyWishlistHtml(wl);
          return;
        }

        try {
          if (typeof colorSwatches === 'function') colorSwatches();
        } catch (e) {
          /* optional theme helper */
        }

        sectionShell.dispatchEvent(
          new CustomEvent('shopify:section:load', {
            bubbles: true,
            detail: { wishlistSectionHtmlInjected: true },
          })
        );

        if (emptySlot) emptySlot.innerHTML = '';

        const missing =
          ids.filter((id) => !byId.has(extractNumericProductId(id))).length + (handles.length - cardsInserted);
        if (missing > 0 && emptySlot) {
          emptySlot.innerHTML = `<p class="wishlist-page__notice" role="status">${escapeHtml(wl.someMissing || '')}</p>`;
        }

        if (U()) U().syncAllButtons();
      })
      .catch((err) => {
        sectionShell.setAttribute('hidden', '');
        const msg =
          err && err.message === 'MISSING_TOKEN'
            ? wl.errorNoToken || 'Configure Storefront API token in theme settings.'
            : wl.errorLoad || (err && err.message) || 'Error';
        if (emptySlot) emptySlot.innerHTML = `<div class="wishlist-page__error" role="alert">${escapeHtml(msg)}</div>`;
      });
  }

  let wishlistPageListenersBound = false;

  function initWishlistPage() {
    const hasGrid = getWishlistSectionMount();
    const hasEmpty = document.getElementById('wishlist-empty-slot');
    if (!hasGrid && !hasEmpty) return;

    if (!wishlistPageListenersBound) {
      wishlistPageListenersBound = true;
      document.addEventListener('wishlist:updated', () => {
        renderWishlistProductGridPage();
      });
    }
    renderWishlistProductGridPage();
  }

  function onDocumentClick(e) {
    if (!S() || !U()) return;
    const btn = e.target.closest('.wishlist-btn');
    if (!btn || !document.body.contains(btn)) return;
    e.preventDefault();
    e.stopPropagation();
    const id = S().normalizeProductId(btn.getAttribute('data-product-id'));
    if (!id) return;

    const wasIn = S().isInWishlist(id);
    const title = btn.getAttribute('data-product-title') || '';

    U().setLoading(btn, true);
    window.requestAnimationFrame(() => {
      S().toggleWishlist(id);
      const nowIn = S().isInWishlist(id);
      const Tr = T();
      if (nowIn && !wasIn && Tr) {
        Tr.trackAddToWishlist(id, title);
        U().toastFromWishlistAction(true);
      } else if (!nowIn && wasIn && Tr) {
        Tr.trackRemoveFromWishlist(id, title);
        U().toastFromWishlistAction(false);
      }
      U().setLoading(btn, false);
      U().animateHeart(btn);
    });
  }

  function boot() {
    runLoggedInMerge();
    if (U()) {
      U().syncAllButtons();
      U().updateCounter();
    }
    initWishlistPage();
  }

  document.addEventListener('click', onDocumentClick);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (U()) {
      U().syncAllButtons();
      U().updateCounter();
    }
    if (event.detail && event.detail.wishlistSectionHtmlInjected) return;
    initWishlistPage();
  });

  window.Wishlist = {
    get WISHLIST_KEY() {
      return S() ? S().WISHLIST_KEY : 'wishlist';
    },
    getWishlist: () => (S() ? S().getWishlist() : []),
    saveWishlist: (ids, meta) => (S() ? S().saveWishlist(ids, meta) : []),
    toggleWishlist: (id) => (S() ? S().toggleWishlist(id) : []),
    isInWishlist: (id) => (S() ? S().isInWishlist(id) : false),
    removeFromWishlist: (id) => (S() ? S().removeFromWishlist(id) : []),
    syncAllButtons: () => (U() ? U().syncAllButtons() : undefined),
    productGidFromNumericId,
    runLoggedInMerge,
    renderWishlistProductGridPage,
  };
})();
